/*
 * Button (Figma 119:13838). Values from get_design_context:
 * | Variant   | Padding        | Radius | Fill / effect                                  | Text                                      |
 * | primary   | 8px 12px       | 8px    | --button-primary-fill, --shadow-button-primary | 550 14/20, white, opacity .9, ss03        |
 * | ghost     | 6px 12px       | 8px    | none (hover --interaction-hover)               | 550 14/20, --text-default, opacity .9, ss03 |
 * | secondary | 8px 12px       | 8px    | --surface-raised, --shadow-button-secondary(-hover) | 550 14/20, --text-default, ss03      |
 * Gap 6px (label ↔ 16px icon). Focus: --focus-m (keyboard only).
 * Not in the design: disabled styling (opacity .5) and secondary layout (taken from primary);
 * on a touch screen (pointer: coarse) at least --size-touch-target (40px) high.
 */
import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react'
import { cx } from './cx'
import { Icon } from './Icon'
import { TintedIcon } from './TintedIcon'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'

const VARIANT: Record<Variant, string> = {
    primary:
        'py-[8px] [background:var(--button-primary-fill)] shadow-[var(--shadow-button-primary)] text-[var(--text-on-accent)] active:brightness-90',
    secondary:
        'py-[8px] bg-[var(--surface-raised)] shadow-[var(--shadow-button-secondary)] hover:shadow-[var(--shadow-button-secondary-hover)] active:bg-[var(--interaction-active)] text-[var(--text-default)]',
    ghost: 'py-[6px] hover:bg-[var(--interaction-hover)] active:bg-[var(--interaction-active)] text-[var(--text-default)]',
    // Not in Figma: a destructive confirmation (delete, stop). The primary's shape in --status-danger.
    danger: 'py-[8px] bg-[var(--status-danger)] shadow-[var(--shadow-button-primary)] text-[var(--text-on-accent)] active:brightness-90',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: Variant
    /** 16px icon after the label (Figma "Icon" slot). */
    trailingIcon?: string
    /** 16px icon before the label, in the label's colour (e.g. "+ New job"). Not in Figma. */
    leadingIcon?: string
    children: ReactNode
    ref?: Ref<HTMLButtonElement>
    /** The action is running: a spinner before the label (and aria-busy); disable it alongside. */
    busy?: boolean
}

export function Button({ variant = 'secondary', trailingIcon, leadingIcon, className, children, type = 'button', busy, ...rest }: ButtonProps) {
    return (
        <button
            type={type}
            className={cx(
                // press: instant feedback on pointer-down (not in Figma; see styles/index.css)
                'press inline-flex shrink-0 cursor-pointer items-center justify-center gap-[6px] overflow-clip rounded-[8px] px-[12px] pointer-coarse:min-h-[var(--size-touch-target)]',
                'focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none',
                'disabled:cursor-not-allowed disabled:opacity-50',
                VARIANT[variant],
                className,
            )}
            aria-busy={busy || undefined}
            {...rest}
        >
            {busy && <span aria-hidden className="spinner opacity-80" />}
            {leadingIcon && !busy && <TintedIcon src={leadingIcon} className={variant !== 'secondary' ? 'opacity-90' : undefined} />}
            <span
                className={cx(
                    'ss03 whitespace-nowrap text-[14px] leading-[20px] font-[550]',
                    variant !== 'secondary' && 'opacity-90',
                )}
            >
                {children}
            </span>
            {trailingIcon && <Icon src={trailingIcon} size={16} />}
        </button>
    )
}
